import React from "react";
import { Link } from "react-router-dom";
import { Thermometer, Truck, ShieldCheck } from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";

const TAB_DEFS = [
  ["overview",   "Overview"],
  ["technical",  "Technical info"],
  ["storage",    "Storage"],
  ["shipping",   "Shipping"],
  ["regulatory", "Research use"],
  ["faq",        "FAQ"],
];

const TECH_ROWS = (product) => [
  ["Public SKU",           product.public_sku],
  ["Strength",             product.strength],
  ["Format",               product.format],
  ...(product.vial_size ? [["Vial size", product.vial_size]] : []),
  ...(product.appearance ? [["Appearance", product.appearance]] : []),
  ["CAS number",           product.cas_number],
  ["Molecular formula",    product.molecular_formula],
  ["Molecular weight",     product.molecular_weight],
  ...(product.purity_percent ? [["Documented purity", product.purity_percent]] : []),
  ["Storage temperature",  product.storage_temperature],
  ...(product.research_category ? [["Research category", product.research_category]] : []),
  ["Compliance status",    product.compliance_status],
  ["Checkout status",      product.checkout_status],
];

const otherNames = (product) => {
  const own = (product.public_display_name || product.product_name || "").toLowerCase();
  return (product.synonyms || []).filter((s) => s && s.toLowerCase() !== own);
};

const FAQ_ROWS = [
  [
    "Do I need an account to purchase?",
    "No — you can check out as a guest. You must be 21+ and be able to sign for delivery. Every order ships with a signature required.",
  ],
  [
    "Do you ship internationally?",
    "International orders are reviewed one by one. Some products or destinations may be restricted; if that applies to your order, we’ll say so before you pay.",
  ],
  [
    "Are batch numbers assigned?",
    "Yes. Every vial is tied to a specific batch. Ask for the batch document (Certificate of Analysis, or COA) by emailing us the batch number from your packing list.",
  ],
];

/**
 * PDP tabbed section — Overview, Technical, Storage, Shipping, Regulatory,
 * FAQs. Certificate-of-Analysis / documentation display was retired from
 * the public site (2026-08); lot documentation is available on request.
 * Kept as `ProductCOASection` file-name for import stability across
 * pages/tests.
 */
// Every panel stays in the HTML (crawlable); inactive ones are only hidden.
const PANEL = "pt-10 data-[state=inactive]:hidden";

export function ProductCOASection({ product }) {
  return (
    <div className="mt-20">
      <Tabs defaultValue="overview">
        <TabsList className="flex flex-wrap gap-1 bg-transparent p-0 border-b border-brand-border rounded-none w-full justify-start h-auto">
          {TAB_DEFS.map(([k, l]) => (
            <TabsTrigger
              key={k}
              value={k}
              data-testid={`tab-${k}`}
              className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-accent data-[state=active]:text-brand-navy data-[state=active]:bg-transparent data-[state=active]:font-semibold text-brand-muted hover:text-brand-navy px-5 py-3.5 text-sm font-medium shadow-none"
            >
              {l}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" forceMount className={`${PANEL} text-brand-text leading-relaxed space-y-5 text-[15px] max-w-3xl`}>
          <p>{product.long_description}</p>
          {otherNames(product).length > 0 && (
            <p data-testid="pdp-other-names">
              <span className="font-semibold text-brand-navy">Also listed as:</span> {otherNames(product).join(", ")}
            </p>
          )}
          {product.research_summary && (
            <p className="text-brand-muted text-sm">{product.research_summary}</p>
          )}
        </TabsContent>

        <TabsContent value="technical" forceMount className={PANEL}>
          <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl">
            {TECH_ROWS(product).map(([k, v]) => (
              <div key={k} className="mps-card p-5">
                <dt className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-muted mb-1.5">
                  {k}
                </dt>
                <dd className="font-mono text-sm text-brand-text">{v || "—"}</dd>
              </div>
            ))}
          </dl>
        </TabsContent>

        <TabsContent value="storage" forceMount className={`${PANEL} max-w-3xl`}>
          <div className="flex items-start gap-4 mps-card p-8">
            <Thermometer size={22} weight="regular" className="text-brand-accent mt-1 shrink-0" />
            <div>
              <div className="font-display font-semibold text-lg text-brand-navy">
                Storage: {product.storage_temperature}
              </div>
              <p className="mt-2 text-brand-muted leading-relaxed">
                Keep away from light. Don’t use the vial if the container
                looks damaged or tampered with. Once reconstituted, follow
                your own laboratory’s stability procedures.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="shipping" forceMount className={`${PANEL} max-w-3xl`}>
          <div className="flex items-start gap-4 mps-card p-8">
            <Truck size={22} weight="regular" className="text-brand-accent mt-1 shrink-0" />
            <div>
              <div className="font-display font-semibold text-lg text-brand-navy">
                Shipping is calculated at checkout
              </div>
              <p className="mt-2 text-brand-muted leading-relaxed">
                Ships as a freeze-dried (lyophilized) powder in a
                tamper-evident box that requires a signature at delivery.
                At checkout you’ll see live rates from every eligible
                carrier — the price and estimated delivery time — before
                you pay. Delivery times are estimates, not guarantees.
              </p>
              <p className="mt-4 text-brand-text">
                <strong className="font-semibold text-brand-navy">A signature is required</strong> at delivery on every order.
              </p>
              <p className="mt-3 text-sm text-brand-muted">
                See the full{" "}
                <Link to="/shipping-policy" className="text-brand-accent hover:underline font-semibold">
                  Shipping Policy
                </Link>
                .
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="regulatory" forceMount className={`${PANEL} max-w-3xl`}>
          <div className="flex items-start gap-4 mps-card p-8">
            <ShieldCheck size={22} weight="regular" className="text-brand-accent mt-1 shrink-0" />
            <div>
              <div className="font-display font-semibold text-lg text-brand-navy">Research use only</div>
              <p className="mt-2 text-brand-muted leading-relaxed">
                Sold only for use in a laboratory research setting. Not
                sold as a drug, food, dietary supplement, cosmetic, or
                medical product. Not for human or animal use.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="faq" forceMount className={`${PANEL} max-w-3xl space-y-4`}>
          {FAQ_ROWS.map(([q, a]) => (
            <div key={q} className="mps-card p-6 lg:p-8">
              <div className="font-display font-semibold text-lg text-brand-navy">{q}</div>
              <div className="mt-2 text-brand-muted leading-relaxed">{a}</div>
            </div>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
